/**
 * Final thumbnail generator with inline PDF.js worker and improved HTML rendering
 */
import { unifiedFetch } from './unifiedFetch'
import * as pdfjsLib from 'pdfjs-dist'

// Use a local copy of the worker to avoid CORS issues
// We'll use the legacy build which includes the worker inline
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url
).href

export interface ThumbnailOptions {
  width?: number
  height?: number
  quality?: number
  allowLocal?: boolean
}

export class FinalThumbnailGenerator {
  private cache: Map<string, string> = new Map()
  
  async generateThumbnail(
    filePath: string,
    options: ThumbnailOptions = {}
  ): Promise<string> {
    const { width = 440, height = 320, quality = 0.92, allowLocal = false } = options
    
    // Check cache
    const cacheKey = `${filePath}-${width}x${height}`
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!
    }
    
    let dataUrl: string
    
    try {
      // Fetch the actual file content
      const result = await unifiedFetch(filePath)
      let content: string | Blob = result.content
      
      // Generate thumbnail based on content
      const ext = filePath.split('.').pop()?.toLowerCase() || ''
      
      console.log(`🎨 Generating thumbnail for ${filePath} (${ext})`, {
        contentType: typeof content,
        isBlob: content instanceof Blob,
        contentSize: content instanceof Blob ? content.size : (content as string).length,
        source: result.source
      })
      
      if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
        dataUrl = await this.generateImageThumbnail(content, ext, width, height, quality)
      } else if (['json', 'jsonl', 'csv', 'tsv', 'txt', 'md', 'py', 'js', 'ts', 'jsx', 'tsx', 'css', 'yaml', 'yml'].includes(ext)) {
        if (content instanceof Blob) {
          content = await content.text()
        }
        dataUrl = await this.generateTextPreview(content as string, ext, width, height)
      } else if (ext === 'html' || ext === 'htm') {
        if (content instanceof Blob) {
          content = await content.text()
        }
        dataUrl = await this.generateHTMLPreviewSafe(content as string, width, height)
      } else if (ext === 'pdf') {
        dataUrl = await this.generatePDFPreviewWithWorker(content, result.url, filePath, width, height)
      } else {
        dataUrl = await this.generateFilePlaceholder(filePath, ext, width, height)
      }
    } catch (error) {
      console.error(`❌ Error generating thumbnail for ${filePath}:`, error)
      const ext = filePath.split('.').pop()?.toLowerCase() || ''
      dataUrl = await this.generateFilePlaceholder(filePath, ext, width, height)
    }
    
    // Cache result
    this.cache.set(cacheKey, dataUrl)
    return dataUrl
  }
  
  private async generateImageThumbnail(
    content: Blob | string,
    ext: string,
    width: number,
    height: number,
    quality: number
  ): Promise<string> {
    return new Promise((resolve) => {
      const img = new Image()
      let url: string
      
      if (content instanceof Blob) {
        url = URL.createObjectURL(content)
      } else if (typeof content === 'string' && content.startsWith('data:')) {
        url = content
      } else {
        url = `data:image/${ext === 'jpg' ? 'jpeg' : ext};base64,${content}`
      }
      
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')!
        
        canvas.width = width
        canvas.height = height
        
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, width, height)
        
        const scale = Math.min(width / img.width, height / img.height, 1)
        const w = img.width * scale
        const h = img.height * scale
        const x = (width - w) / 2
        const y = (height - h) / 2
        
        ctx.drawImage(img, x, y, w, h)
        
        ctx.strokeStyle = '#e2e8f0'
        ctx.lineWidth = 1
        ctx.strokeRect(0, 0, width, height)
        
        const result = canvas.toDataURL('image/jpeg', quality)
        if (content instanceof Blob) {
          URL.revokeObjectURL(url)
        }
        resolve(result)
      }
      
      img.onerror = () => {
        console.error('Failed to load image')
        if (content instanceof Blob) {
          URL.revokeObjectURL(url)
        }
        resolve(this.generateFilePlaceholder('image', ext, width, height))
      }
      
      img.src = url
    })
  }
  
  private async generateTextPreview(
    content: string,
    ext: string,
    width: number,
    height: number
  ): Promise<string> {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!
    
    canvas.width = width
    canvas.height = height
    
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
    
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    
    if (ext === 'json' || ext === 'jsonl') {
      ctx.font = '13px Monaco, Consolas, monospace'
      
      let lines: string[] = []
      if (ext === 'json') {
        try {
          const parsed = JSON.parse(content)
          lines = JSON.stringify(parsed, null, 2).split('\n')
        } catch {
          lines = content.split('\n')
        }
      } else {
        lines = []
        const jsonlLines = content.split('\n').filter(l => l.trim())
        jsonlLines.slice(0, 3).forEach((line, i) => {
          try {
            const obj = JSON.parse(line)
            lines.push(`// Object ${i + 1}`)
            lines.push(...JSON.stringify(obj, null, 2).split('\n'))
            lines.push('')
          } catch {
            lines.push(line)
          }
        })
      }
      
      let y = 15
      const maxLines = Math.floor((height - 20) / 16)
      
      lines.slice(0, maxLines).forEach(line => {
        const trimmed = line
        let x = 10
        
        if (trimmed.includes('"') && trimmed.includes(':')) {
          const match = trimmed.match(/^(\s*)(".*?")\s*:\s*(.*)$/)
          if (match) {
            ctx.fillStyle = 'transparent'
            ctx.fillText(match[1], x, y)
            x += ctx.measureText(match[1]).width
            
            ctx.fillStyle = '#0969da'
            ctx.fillText(match[2], x, y)
            x += ctx.measureText(match[2]).width
            
            ctx.fillStyle = '#1f2328'
            ctx.fillText(': ', x, y)
            x += ctx.measureText(': ').width
            
            const value = match[3]
            if (value.startsWith('"')) {
              ctx.fillStyle = '#0a3069'
            } else if (value.match(/^(true|false|null)/)) {
              ctx.fillStyle = '#cf222e'
            } else if (value.match(/^-?\d/)) {
              ctx.fillStyle = '#0550ae'
            } else {
              ctx.fillStyle = '#1f2328'
            }
            
            const maxWidth = width - x - 10
            let displayValue = value
            if (ctx.measureText(value).width > maxWidth) {
              while (ctx.measureText(displayValue + '...').width > maxWidth && displayValue.length > 0) {
                displayValue = displayValue.slice(0, -1)
              }
              displayValue += '...'
            }
            ctx.fillText(displayValue, x, y)
          } else {
            ctx.fillStyle = '#1f2328'
            const maxWidth = width - 20
            let displayLine = trimmed
            if (ctx.measureText(trimmed).width > maxWidth) {
              while (ctx.measureText(displayLine + '...').width > maxWidth && displayLine.length > 0) {
                displayLine = displayLine.slice(0, -1)
              }
              displayLine += '...'
            }
            ctx.fillText(displayLine, x, y)
          }
        } else {
          ctx.fillStyle = trimmed.trim().startsWith('//') ? '#6e7781' : '#1f2328'
          ctx.fillText(trimmed.slice(0, 60), x, y)
        }
        
        y += 16
      })
    } else if (ext === 'csv' || ext === 'tsv') {
      const delimiter = ext === 'tsv' ? '\t' : ','
      const rows = content.split('\n').slice(0, 15)
      
      ctx.strokeStyle = '#e2e8f0'
      ctx.lineWidth = 0.5
      
      const cellHeight = 20
      const maxCols = 6
      
      rows.forEach((row, rowIndex) => {
        const cells = row.split(delimiter).slice(0, maxCols)
        const y = rowIndex * cellHeight + 10
        
        if (y > height - cellHeight) return
        
        if (rowIndex === 0) {
          ctx.fillStyle = '#f8f9fa'
          ctx.fillRect(0, y, width, cellHeight)
        }
        
        cells.forEach((cell, colIndex) => {
          const cellWidth = width / maxCols
          const x = colIndex * cellWidth
          
          ctx.strokeRect(x, y, cellWidth, cellHeight)
          
          ctx.fillStyle = rowIndex === 0 ? '#0969da' : '#1f2328'
          ctx.font = rowIndex === 0 ? 'bold 12px sans-serif' : '12px sans-serif'
          
          let text = cell.trim()
          const maxWidth = cellWidth - 8
          if (ctx.measureText(text).width > maxWidth) {
            while (ctx.measureText(text + '...').width > maxWidth && text.length > 0) {
              text = text.slice(0, -1)
            }
            text += '...'
          }
          
          ctx.fillText(text, x + 4, y + 14)
        })
      })
    } else if (ext === 'md' || ext === 'markdown') {
      const mdLines = content.split('\n')
      
      let y = 20
      mdLines.slice(0, 20).forEach(line => {
        if (y > height - 20) return
        
        if (line.startsWith('# ')) {
          ctx.font = 'bold 18px -apple-system, sans-serif'
          ctx.fillStyle = '#1e293b'
          ctx.fillText(line.slice(2).slice(0, 40), 15, y)
          y += 25
        } else if (line.startsWith('## ')) {
          ctx.font = 'bold 16px -apple-system, sans-serif'
          ctx.fillStyle = '#334155'
          ctx.fillText(line.slice(3).slice(0, 45), 15, y)
          y += 22
        } else if (line.startsWith('### ')) {
          ctx.font = 'bold 14px -apple-system, sans-serif'
          ctx.fillStyle = '#475569'
          ctx.fillText(line.slice(4).slice(0, 50), 15, y)
          y += 20
        } else if (line.startsWith('- ') || line.startsWith('* ')) {
          ctx.font = '13px -apple-system, sans-serif'
          ctx.fillStyle = '#475569'
          ctx.fillText('• ' + line.slice(2).slice(0, 55), 20, y)
          y += 18
        } else if (line.trim()) {
          ctx.font = '13px -apple-system, sans-serif'
          ctx.fillStyle = '#475569'
          ctx.fillText(line.slice(0, 60), 15, y)
          y += 18
        } else {
          y += 10
        }
      })
    } else {
      ctx.font = '12px Monaco, Consolas, monospace'
      
      const lines = content.split('\n')
      let y = 15
      const maxLines = Math.floor((height - 20) / 15)
      
      lines.slice(0, maxLines).forEach(line => {
        const trimmed = line
        
        if (/^\s*(import|export|from|const|let|var|function|class|def|if|else|for|while|return)/.test(trimmed)) {
          ctx.fillStyle = '#cf222e'
        } else if (trimmed.trim().startsWith('//') || trimmed.trim().startsWith('#')) {
          ctx.fillStyle = '#6e7781'
        } else if (/".*"|'.*'|`.*`/.test(trimmed)) {
          ctx.fillStyle = '#0a3069'
        } else {
          ctx.fillStyle = '#1f2328'
        }
        
        const maxWidth = width - 20
        let displayLine = trimmed
        if (ctx.measureText(trimmed).width > maxWidth) {
          while (ctx.measureText(displayLine + '...').width > maxWidth && displayLine.length > 0) {
            displayLine = displayLine.slice(0, -1)
          }
          displayLine += '...'
        }
        
        ctx.fillText(displayLine, 10, y)
        y += 15
      })
    }
    
    ctx.strokeStyle = '#e2e8f0'
    ctx.lineWidth = 1
    ctx.strokeRect(0, 0, width, height)
    
    return canvas.toDataURL('image/png', 0.9)
  }
  
  private async generateHTMLPreviewSafe(
    htmlContent: string,
    width: number,
    height: number
  ): Promise<string> {
    console.log('📄 Generating HTML preview (safe method)')
    
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!
    
    canvas.width = width
    canvas.height = height
    
    // White background
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
    
    // Parse HTML for content extraction
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlContent, 'text/html')
    
    // Draw browser chrome
    ctx.fillStyle = '#f3f4f6'
    ctx.fillRect(0, 0, width, 30)
    
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(10, 8, width - 20, 14)
    
    const title = doc.title || 'HTML Document'
    ctx.fillStyle = '#6b7280'
    ctx.font = '11px sans-serif'
    ctx.fillText('🔒 ' + title.slice(0, 40), 15, 19)
    
    // Extract and render content
    let y = 45
    
    // Get background color if set
    const bodyStyle = doc.body?.getAttribute('style')
    if (bodyStyle) {
      const bgMatch = bodyStyle.match(/background(?:-color)?:\s*([^;]+)/i)
      if (bgMatch) {
        ctx.fillStyle = bgMatch[1].trim()
        ctx.fillRect(0, 30, width, height - 30)
      }
    }
    
    // Render headings and paragraphs
    const elements = doc.body?.querySelectorAll('h1, h2, h3, h4, h5, h6, p, li, div, span') || []
    const rendered = new Set()
    
    elements.forEach(el => {
      if (y > height - 20) return
      if (rendered.has(el)) return
      
      // Skip if parent was already rendered
      let parent = el.parentElement
      while (parent && parent !== doc.body) {
        if (rendered.has(parent)) return
        parent = parent.parentElement
      }
      
      const text = el.textContent?.trim()
      if (!text) return
      
      rendered.add(el)
      
      const tagName = el.tagName.toLowerCase()
      
      // Set font based on tag
      if (tagName === 'h1') {
        ctx.font = 'bold 18px -apple-system, sans-serif'
        ctx.fillStyle = '#111827'
      } else if (tagName === 'h2') {
        ctx.font = 'bold 16px -apple-system, sans-serif'
        ctx.fillStyle = '#1f2937'
      } else if (tagName === 'h3') {
        ctx.font = 'bold 14px -apple-system, sans-serif'
        ctx.fillStyle = '#374151'
      } else if (tagName === 'h4' || tagName === 'h5' || tagName === 'h6') {
        ctx.font = 'bold 13px -apple-system, sans-serif'
        ctx.fillStyle = '#4b5563'
      } else if (tagName === 'li') {
        ctx.font = '12px -apple-system, sans-serif'
        ctx.fillStyle = '#4b5563'
        ctx.fillText('• ', 15, y)
      } else {
        ctx.font = '12px -apple-system, sans-serif'
        ctx.fillStyle = '#4b5563'
      }
      
      // Check for inline styles
      const inlineStyle = el.getAttribute('style')
      if (inlineStyle) {
        const colorMatch = inlineStyle.match(/color:\s*([^;]+)/i)
        if (colorMatch) {
          ctx.fillStyle = colorMatch[1].trim()
        }
      }
      
      // Draw text with word wrap
      const maxWidth = width - 30
      const words = text.split(' ')
      let line = ''
      let lineCount = 0
      const maxLines = tagName.startsWith('h') ? 2 : 3
      
      for (const word of words) {
        if (lineCount >= maxLines) break
        
        const testLine = line ? `${line} ${word}` : word
        if (ctx.measureText(testLine).width > maxWidth && line) {
          ctx.fillText(line, tagName === 'li' ? 25 : 15, y)
          y += tagName.startsWith('h') ? 22 : 16
          lineCount++
          line = word
        } else {
          line = testLine
        }
      }
      
      if (line && lineCount < maxLines) {
        ctx.fillText(line, tagName === 'li' ? 25 : 15, y)
        y += tagName.startsWith('h') ? 26 : 18
      }
    })
    
    // Border
    ctx.strokeStyle = '#e2e8f0'
    ctx.lineWidth = 1
    ctx.strokeRect(0, 0, width, height)
    
    return canvas.toDataURL('image/png', 0.95)
  }
  
  private async generatePDFPreviewWithWorker(
    content: Blob | string,
    url: string,
    filePath: string,
    width: number,
    height: number
  ): Promise<string> {
    console.log('📑 Starting PDF preview generation for:', filePath)
    
    try {
      let pdfData: ArrayBuffer
      
      // Convert content to ArrayBuffer
      if (content instanceof Blob) {
        console.log('  Converting Blob to ArrayBuffer...')
        pdfData = await content.arrayBuffer()
      } else if (typeof content === 'string') {
        console.log('  Converting base64 string to ArrayBuffer...')
        const binaryString = atob(content)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }
        pdfData = bytes.buffer
      } else {
        throw new Error('Invalid PDF content type')
      }
      
      // Check PDF header
      const header = new Uint8Array(pdfData.slice(0, 5))
      const headerStr = String.fromCharCode(...header)
      console.log('  PDF header:', headerStr)
      
      if (!headerStr.startsWith('%PDF')) {
        throw new Error('Invalid PDF file')
      }
      
      console.log('  Loading PDF with pdf.js...')
      const loadingTask = pdfjsLib.getDocument({ 
        data: pdfData,
        // Disable range requests since we have the full data
        disableRange: true,
        disableStream: true,
      })
      
      const pdf = await loadingTask.promise
      console.log('  PDF loaded. Pages:', pdf.numPages)
      
      const page = await pdf.getPage(1)
      const viewport = page.getViewport({ scale: 1.0 })
      const scale = Math.min(width / viewport.width, height / viewport.height) * 0.9
      const scaledViewport = page.getViewport({ scale })
      
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')!
      canvas.width = width
      canvas.height = height
      
      // White background
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, width, height)
      
      // Center the PDF
      const xOffset = (width - scaledViewport.width) / 2
      const yOffset = (height - scaledViewport.height) / 2
      
      // Create render context
      const renderContext = {
        canvasContext: ctx,
        viewport: scaledViewport,
        transform: [1, 0, 0, 1, xOffset, yOffset]
      }
      
      await page.render(renderContext as any).promise
      console.log('  ✅ PDF rendered successfully')
      
      // Add border
      ctx.strokeStyle = '#e2e8f0'
      ctx.lineWidth = 1
      ctx.strokeRect(0, 0, width, height)
      
      // Add PDF badge
      ctx.fillStyle = 'rgba(220, 38, 38, 0.9)'
      ctx.fillRect(width - 55, 10, 45, 25)
      ctx.fillStyle = '#ffffff'
      ctx.font = 'bold 11px sans-serif'
      ctx.textAlign = 'center'
      ctx.fillText('PDF', width - 32, 26)
      
      // Cleanup
      pdf.destroy()
      
      return canvas.toDataURL('image/png', 0.9)
    } catch (error) {
      console.error('❌ PDF preview generation failed:', error)
      return this.generatePDFPlaceholder(width, height)
    }
  }
  
  private generatePDFPlaceholder(width: number, height: number): string {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!
    
    canvas.width = width
    canvas.height = height
    
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
    
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)'
    ctx.fillRect(32, 32, width - 60, height - 60)
    
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(30, 30, width - 60, height - 60)
    ctx.strokeStyle = '#e0e0e0'
    ctx.strokeRect(30, 30, width - 60, height - 60)
    
    let y = 55
    for (let i = 0; i < 8; i++) {
      const lineWidth = Math.random() * 0.3 + 0.6
      ctx.fillStyle = '#e5e7eb'
      ctx.fillRect(45, y, (width - 90) * lineWidth, 8)
      y += 16
    }
    
    ctx.fillStyle = '#dc2626'
    ctx.fillRect(width - 70, 15, 55, 35)
    ctx.fillStyle = '#ffffff'
    ctx.font = 'bold 14px sans-serif'
    ctx.textAlign = 'center'
    ctx.fillText('PDF', width - 42, 37)
    
    ctx.strokeStyle = '#e2e8f0'
    ctx.lineWidth = 1
    ctx.strokeRect(0, 0, width, height)
    
    return canvas.toDataURL('image/png', 0.9)
  }
  
  private async generateFilePlaceholder(
    fileName: string,
    ext: string,
    width: number,
    height: number
  ): Promise<string> {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!
    
    canvas.width = width
    canvas.height = height
    
    const colors = this.getFileTypeColors(ext)
    
    const gradient = ctx.createLinearGradient(0, 0, width, height)
    gradient.addColorStop(0, colors.bg1)
    gradient.addColorStop(1, colors.bg2)
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, width, height)
    
    this.drawFileIcon(ctx, width, height, colors)
    
    ctx.fillStyle = colors.text
    ctx.font = 'bold 18px -apple-system, system-ui, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(ext.toUpperCase(), width / 2, height / 2 + 50)
    
    ctx.strokeStyle = colors.border
    ctx.lineWidth = 1
    ctx.strokeRect(0, 0, width, height)
    
    return canvas.toDataURL('image/png', 0.9)
  }
  
  private getFileTypeColors(ext: string) {
    const typeMap: Record<string, any> = {
      pdf: { bg1: '#fee2e2', bg2: '#fecaca', icon: '#dc2626', text: '#991b1b', border: '#fca5a5' },
      json: { bg1: '#e0f2fe', bg2: '#bae6fd', icon: '#0284c7', text: '#075985', border: '#7dd3fc' },
      csv: { bg1: '#dcfce7', bg2: '#bbf7d0', icon: '#16a34a', text: '#166534', border: '#86efac' },
      default: { bg1: '#f1f5f9', bg2: '#e2e8f0', icon: '#64748b', text: '#475569', border: '#cbd5e1' }
    }
    return typeMap[ext] || typeMap.default
  }
  
  private drawFileIcon(ctx: CanvasRenderingContext2D, width: number, height: number, colors: any) {
    const iconSize = 60
    const iconX = (width - iconSize) / 2
    const iconY = (height - iconSize) / 2 - 20
    
    ctx.fillStyle = 'white'
    ctx.fillRect(iconX, iconY, iconSize, iconSize)
    ctx.strokeStyle = colors.icon
    ctx.lineWidth = 2
    ctx.strokeRect(iconX, iconY, iconSize, iconSize)
  }
  
  clearCache() {
    this.cache.clear()
  }
}

export const finalThumbnailGenerator = new FinalThumbnailGenerator()